import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { EventLogEntry, EventLogQueryFilters } from '@mariana/labos-client';
import { limsClient } from '../api/limsClient';
import { Breadcrumb } from './Breadcrumb';
import { EventsTable } from './EventsTable';
import { PaginationControls } from './PaginationControls';
import { useURLSyncedFilters } from '../hooks/useURLSyncedFilters';
import { useLab } from '../LabContext';
import './EventsView.css';

const ENTITY_TYPES = ['sample', 'test', 'assay', 'order', 'attachment'] as const;
const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'processed', label: 'Processed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'error', label: 'Error' },
];

export const EventsView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventLabId } = useLab();

  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);

  const {
    filters,
    handleFilterChange,
    handleClearFilters,
    handlePageChange,
    handleLimitChange,
    hasActiveFilters,
  } = useURLSyncedFilters<Omit<EventLogQueryFilters, 'limit' | 'offset'>>({
    defaults: {
      status: undefined,
      entityType: undefined,
      entityId: undefined,
      listenerName: undefined,
    },
    paramKeyMap: {
      entity_type: 'entityType',
      entity_id: 'entityId',
      listener_name: 'listenerName',
    },
    parseParam: (key, value) => (key === 'entityId' ? Number(value) : value),
  });

  const limit = filters.limit;
  const offset = filters.offset;

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await limsClient.getEvents({ ...filters, labId: eventLabId });
        setEvents(response.data);
        setCount(response.pagination.count);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load events';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [searchParams, eventLabId]);

  return (
    <div className="events-view">
      <div className="events-view-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Activity' }, { label: 'Events' }]} />
        <span className="events-header-info">
          {count > 0 ? `${offset + 1}–${Math.min(offset + limit, count)} of ${count}` : 'No events'}
        </span>
        <div className="events-header-controls">
          <label className="events-limit-label">
            Per page:
            <select className="limit-select" value={limit} onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
        </div>
        {error && <div className="error-message">Error: {error}</div>}
      </div>

      <div className="events-filters">
        <div className="events-filter-group">
          <label>Status:</label>
          <select
            className="events-filter-select"
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="events-filter-group">
          <label>Entity Type:</label>
          <select
            className="events-filter-select"
            value={filters.entityType || ''}
            onChange={(e) => handleFilterChange('entityType', e.target.value)}
          >
            <option value="">All types</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="events-filter-group">
          <label>Entity ID:</label>
          <input
            type="number"
            className="events-filter-input"
            placeholder="ID"
            value={filters.entityId ?? ''}
            onChange={(e) => handleFilterChange('entityId', e.target.value)}
          />
        </div>

        <div className="events-filter-group">
          <label>Listener:</label>
          <input
            type="text"
            className="events-filter-input"
            placeholder="Listener name"
            value={filters.listenerName || ''}
            onChange={(e) => handleFilterChange('listenerName', e.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button className="events-filter-clear" onClick={handleClearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className="events-table-container">
        {loading ? (
          <div className="events-loading">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        ) : (
          <div className="events-table-wrapper">
            <EventsTable
              events={events}
              onEntityClick={(type, id) => navigate(`/${type}/${id}`)}
            />
          </div>
        )}

        {!loading && events.length > 0 && (
          <PaginationControls
            offset={offset}
            limit={limit}
            count={count}
            onPageChange={handlePageChange}
            className="events-pagination"
          />
        )}
      </div>
    </div>
  );
};
